import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OnboardingDialogComponent } from '@app/+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.component';
import {
  OnboardingDialogAction,
  OnboardingDialogResult,
  OnboardingDialogStep,
} from '@app/+authenticated/shared/onboarding/onboarding-dialog/onboarding-dialog.model';
import { SelfOnboardingSegment } from '@app/enums';
import { hasAtleastSubscriptionPlan } from '@app/reducers/account/account-subscription.helper';
import { PlanType, SubscriptionModel } from '@app/reducers/account/account-subscription.model';
import { getAccountSubscription, getSelfOnboarding } from '@app/reducers/account/account.service';
import { TrackingService } from '@app/services/tracking.service';
import { DragDropData } from '@app/shared/drag-drop/models';
import {
  ActivationOnboardingStep,
  InternalOnboardingStep,
  OnboardingChaptersCTA,
  OnboardingChecklistStepConfiguration,
  OnboardingStep,
  SmbHospitalityOnboardingChecklistStep,
  smbHospitalityOnboardingChecklistStepConfiguration,
} from '@app/shared/onboarding/onboarding-checklist.model';
import { watchState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/index';
import { SbDialogService } from '@sb/ui';
import { filter, of, switchMap, take } from 'rxjs';

import { AbsenceManagementDialogStep } from './chapters/absence-management/absence-management.model';
import { AbsenceManagementOnboardingService } from './chapters/services/absence-management-onboarding.service';
import { MobileAppOnboardingService } from './chapters/services/mobile-app-onboarding.service';
import { OptimizedScheduleOnboardingService } from './chapters/services/optimized-schedule-onboarding.service';
import { PayrollOnboardingService } from './chapters/services/payroll-onboarding.service';
import { ScheduleDropHandlerResult, ScheduleOnboardingService } from './chapters/services/schedule-onboarding.service';
import { TargetSettingOnboardingService } from './chapters/services/target-setting-onboarding.service';
import { TimeTrackingOnboardingService } from './chapters/services/time-tracking-onboarding.service';
import {
  InviteEmployeesDialogComponent,
  InviteEmployeesDialogResult,
} from './components/invite-employees/invite-employees.component';
import { OnboardingStepConfig } from './onboarding.model';
import { OnboardingStore } from './onboarding.store';
import { OnboardingUtils } from './onboarding.utils';

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  public selfOnboardingSegment: SelfOnboardingSegment;
  public isOnboarding: boolean; // Purely used for the components

  private subscription: SubscriptionModel;

  private customNextStepCTAs: OnboardingChaptersCTA;

  private onboardingActions: Record<OnboardingStep, (args?: OnboardingStepConfig) => void> = {
    [SmbHospitalityOnboardingChecklistStep.INVITE_TEAM]: () => this.openOnboardingInviteEmployeesDialog(),
    [SmbHospitalityOnboardingChecklistStep.OPTIMIZED_SCHEDULE]: (args) =>
      this.openOnboardingOptimizedScheduleDialog(args),
    [InternalOnboardingStep.OPTIMIZED_SCHEDULE_DEMO]: (args) => this.openOnboardingOptimizedScheduleDemo(args),
    [InternalOnboardingStep.OPTIMIZED_SCHEDULE_SUMMARY]: (args) =>
      this.openOnboardingOptimizedScheduleSummaryDialog(args),
    [SmbHospitalityOnboardingChecklistStep.TIME_TRACKING]: (args) => this.openOnboardingTimeTrackingDialog(args),
    [SmbHospitalityOnboardingChecklistStep.ABSENCE_MANAGEMENT]: (args) =>
      this.openOnboardingAbsenceManagementDialog(args),
    [InternalOnboardingStep.ABSENCE_MANAGEMENT_DEMO]: (args) => this.openOnboardingAbsenceManagementDemo(args),
    [InternalOnboardingStep.ABSENCE_MANAGEMENT_SETUP]: (args) =>
      this.openOnboardingAbsenceManagementDialog(args, AbsenceManagementDialogStep.BASIC_SETUP),
    [SmbHospitalityOnboardingChecklistStep.PAYROLL]: (args) => this.openOnboardingPayrollDialog(args),
    [SmbHospitalityOnboardingChecklistStep.MOBILE_APP]: (args) => this.openOnboardingMobileAppDialog(args),
    [SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT]: () => this.scheduleOnboardingService.openDialog(),
    [InternalOnboardingStep.TIME_TRACKING_DEMO]: (args) => this.openOnboardingTimeTrackingDemo(args),
    [InternalOnboardingStep.CELEBRATION_INVITED]: () => this.openFinishedOnboardingCelebratoryDialog('primary'),
    [InternalOnboardingStep.CELEBRATION_SKIPPED]: () => this.openFinishedOnboardingCelebratoryDialog('secondary'),
    [InternalOnboardingStep.TIME_TRACKING_SUMMARY]: (args) => this.openOnboardingTimeTrackingSummaryDialog(args),
    [ActivationOnboardingStep.TARGET_SETTING]: (args) => this.openOnboardingTargetSettingDialog(args),
  };

  public constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly trackingService: TrackingService,
    private readonly dialogService: SbDialogService,
    private readonly scheduleOnboardingService: ScheduleOnboardingService,
    private readonly absenceManagementOnboardingService: AbsenceManagementOnboardingService,
    private readonly payrollOnboardingService: PayrollOnboardingService,
    private readonly optimizedScheduleOnboardingService: OptimizedScheduleOnboardingService,
    private readonly timeTrackingOnboardingService: TimeTrackingOnboardingService,
    private readonly targetSettingOnboardingService: TargetSettingOnboardingService,
    private readonly mobileAppOnboardingService: MobileAppOnboardingService,
    private readonly onboardingUtils: OnboardingUtils,
  ) {
    const onboardingStore = inject(OnboardingStore);
    watchState(onboardingStore, ({ step, config }) => {
      if (!step) return;
      this.performOnboardingStep(step, config);
    });

    void this.store
      .select(getSelfOnboarding)
      .pipe(take(1))
      .subscribe((selfOnboardingSegment) => {
        this.selfOnboardingSegment = selfOnboardingSegment;

        this.scheduleOnboardingService.setSelfOnboardingSegment(selfOnboardingSegment);
      });

    void this.store
      .select(getAccountSubscription)
      .pipe(
        filter((subscription) => !!subscription),
        take(1),
      )
      .subscribe((subscription: SubscriptionModel) => {
        this.subscription = subscription;
      });
  }

  public finishStep(action: OnboardingDialogAction) {
    const nextStep = this.getNextStep(action);

    if (!nextStep) {
      this.onboardingUtils.stripOnboardingParamsFromRoute();
      return;
    }
    this.performOnboardingStep(nextStep);
  }

  public finishScheduleShiftStep(response, dragDropData: DragDropData) {
    if (this.scheduleOnboardingService.waitingForShiftScheduled) {
      void this.scheduleOnboardingService
        .scheduleDropHandler(response, dragDropData)
        .pipe(
          take(1),
          // Make sure the right shift type was scheduled
          filter(
            (result) => ![ScheduleDropHandlerResult.SKIP, ScheduleDropHandlerResult.SHIFT_NOT_FOUND].includes(result),
          ),
        )
        .subscribe(() => this.finishStep(this.isOnboarding ? 'primary' : undefined));
    }
  }

  private getNextStep(action: OnboardingDialogAction) {
    return this.chapterCTAs?.[action]?.step;
  }

  // If the chapter has an explicit CTA configuration, we prioritize it, otherwise return the nextPriorityCTA
  public get chapterCTAs() {
    return this.customNextStepCTAs || this.onboardingUtils.nextPriorityCTA;
  }

  public performOnboardingStep(step: OnboardingStep, args?: OnboardingStepConfig) {
    step = step || SmbHospitalityOnboardingChecklistStep.SCHEDULE_SHIFT;

    const config: OnboardingChecklistStepConfiguration = smbHospitalityOnboardingChecklistStepConfiguration[step];
    this.customNextStepCTAs = config?.customNextStep;

    if (config?.requiredRoute && !this.router.isActive(config.requiredRoute, false)) {
      this.router.navigate([config.requiredRoute, { onboardingStep: step }]);
      return;
    }

    const action = this.onboardingActions[step];
    if (action) action(args);
  }

  public initializeOnboardingVariables(originator: string, onboardingStep?: SmbHospitalityOnboardingChecklistStep) {
    this.isOnboarding = originator === 'onboarding' && !onboardingStep;
    this.performOnboardingStep(onboardingStep);
  }

  private openOnboardingOptimizedScheduleDialog(args: OnboardingStepConfig = {}) {
    if (!args?.originator) {
      args.originator = 'onboarding';
    }

    void this.optimizedScheduleOnboardingService
      .openDialog(args?.originator)
      .subscribe((result: OnboardingDialogResult) => {
        if (result?.action) {
          this.performOnboardingStep(InternalOnboardingStep.OPTIMIZED_SCHEDULE_DEMO, args);
        }
      });
  }

  private openOnboardingAbsenceManagementDialog(originator, step?: AbsenceManagementDialogStep): void {
    // Skip marking it as complete by default
    let completeChapter$ = of(true);

    if (
      step === AbsenceManagementDialogStep.BASIC_SETUP &&
      !hasAtleastSubscriptionPlan(PlanType.PREMIUM, this.subscription)
    ) {
      // Skip basic setup for non-premium accounts
      step = AbsenceManagementDialogStep.SUMMARY;
      completeChapter$ = this.absenceManagementOnboardingService.completeChapter({ originator });
    }

    void completeChapter$
      .pipe(
        switchMap(() => this.absenceManagementOnboardingService.openDialog(originator, step)),
        take(1),
      )
      .subscribe((result: OnboardingDialogResult) => {
        if ((step === undefined || step === AbsenceManagementDialogStep.TEASER) && result?.action === 'primary') {
          this.performOnboardingStep(InternalOnboardingStep.ABSENCE_MANAGEMENT_DEMO, { originator });
          return;
        }

        this.finishStep(result?.action);
      });
  }

  private openOnboardingTargetSettingDialog(args: OnboardingStepConfig) {
    void this.targetSettingOnboardingService.openDialog(args?.originator).closed.subscribe(() => {
      this.onboardingUtils.stripOnboardingParamsFromRoute();
    });
  }

  private openOnboardingTimeTrackingDialog(args: OnboardingStepConfig) {
    void this.timeTrackingOnboardingService.openDialog(args?.originator).subscribe((result: OnboardingDialogResult) => {
      if (result?.action) {
        this.performOnboardingStep(InternalOnboardingStep.TIME_TRACKING_DEMO, args);
      }
    });
  }

  private openOnboardingPayrollDialog(args: OnboardingStepConfig) {
    void this.payrollOnboardingService.openDialog(args?.originator).subscribe((result: OnboardingDialogResult) => {
      if (result) {
        this.finishStep(result.action);
      }
    });
  }

  private openOnboardingOptimizedScheduleDemo(args) {
    void this.optimizedScheduleOnboardingService.openDemo(args?.skipFlow).subscribe(() => {
      if (!args?.skipFlow) {
        this.performOnboardingStep(InternalOnboardingStep.OPTIMIZED_SCHEDULE_SUMMARY, args);
      }
    });
  }

  private openOnboardingAbsenceManagementDemo(args) {
    void this.absenceManagementOnboardingService.openDemo().subscribe(() => {
      if (!args?.skipFlow) {
        this.performOnboardingStep(InternalOnboardingStep.ABSENCE_MANAGEMENT_SETUP, args);
      }
    });
  }

  private openOnboardingTimeTrackingDemo(args) {
    void this.timeTrackingOnboardingService.openDemo(args?.skipFlow).subscribe(() => {
      if (!args?.skipFlow) {
        this.performOnboardingStep(InternalOnboardingStep.TIME_TRACKING_SUMMARY, args);
      }
    });
  }

  private openOnboardingMobileAppDialog(args: OnboardingStepConfig) {
    void this.mobileAppOnboardingService.openDialog(args?.originator).subscribe((result: OnboardingDialogResult) => {
      if (result) {
        this.finishStep(this.isOnboarding ? result?.action : undefined);
      }
    });
  }

  private openOnboardingTimeTrackingSummaryDialog(args: OnboardingStepConfig) {
    void this.timeTrackingOnboardingService
      .openSummary(args?.originator)
      .subscribe((result: OnboardingDialogResult) => {
        this.finishStep(result?.action);
      });
  }

  private openOnboardingOptimizedScheduleSummaryDialog(args: OnboardingStepConfig) {
    void this.optimizedScheduleOnboardingService
      .openSummary(args?.originator)
      .subscribe((result: OnboardingDialogResult) => {
        this.finishStep(result?.action);
      });
  }

  private openOnboardingInviteEmployeesDialog() {
    void this.dialogService
      .setSize('lg')
      .open(InviteEmployeesDialogComponent, {
        disableClose: true,
      })
      .closed.pipe(take(1))
      .subscribe((result: InviteEmployeesDialogResult) => {
        const event =
          result.action === 'primary'
            ? 'Onboarding Scheduling Invite Team Completed'
            : 'Onboarding Scheduling Invite Team Skipped';

        this.trackingService.event(event, {
          onboarding_segment: this.selfOnboardingSegment,
          employee_id_count: result.count,
        });

        this.finishStep(result?.action);
      });
  }

  private openFinishedOnboardingCelebratoryDialog(action: OnboardingDialogAction) {
    void this.dialogService
      .setSize('sm')
      .open(OnboardingDialogComponent, {
        disableClose: true,
        data: {
          step:
            action === 'primary' ? OnboardingDialogStep.FINISH_INVITED : OnboardingDialogStep.FINISH_INVITES_SKIPPED,
        },
      })
      .closed.subscribe((result: OnboardingDialogResult) => {
        switch (result.action) {
          case 'primary':
            this.trackingService.event('Onboarding Scheduling Exploring Continued', {
              onboarding_segment: this.selfOnboardingSegment,
            });
            break;
          case 'secondary':
            this.trackingService.event('Onboarding Scheduling Help Center Opened', {
              onboarding_segment: this.selfOnboardingSegment,
            });
            this.router.navigate(['', { outlets: { modal: ['support'] } }]);
            break;
        }
      });
  }
}
